import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

// Keep in sync with upgrade-modal's CREDITS_PER_AD — rough credits per finished ad.
const CREDITS_PER_AD = 80;

type Pack = { id: string; amountCents: number; credits: number };

/**
 * One-time credit top-up. Opened from the "Top Up Account" link in the Upgrade
 * modal (existing customers only). Each pack → a payment-mode Stripe Checkout;
 * credits are granted on return via App.tsx's /?topup=success handler.
 */
export function TopUpModal({ onClose }: { onClose: () => void }) {
  const { toast } = useToast();
  const [buyingId, setBuyingId] = useState<string | null>(null);

  const { data: packs, isLoading } = useQuery<Pack[]>({
    queryKey: ["/api/billing/topup-packs"],
    queryFn: async () => {
      const res = await fetch("/api/billing/topup-packs", { credentials: "include" });
      if (!res.ok) throw new Error(String(res.status));
      return res.json();
    },
  });

  const buy = async (packId: string) => {
    setBuyingId(packId);
    try {
      const res = await fetch("/api/billing/topup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ packId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) throw new Error(data?.message || "Couldn’t start checkout");
      window.location.href = data.url; // → Stripe Checkout (one-time payment)
    } catch (err: any) {
      toast({ title: "Couldn’t start checkout", description: err.message ?? "Please try again.", variant: "destructive" });
      setBuyingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Top up credits</h2>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-800" data-testid="topup-modal-close">✕</button>
        </div>
        <p className="mb-4 text-xs text-neutral-500">
          One-time purchase — credits are added to your balance right away and don’t reset monthly.
        </p>

        {isLoading && <div className="p-8 text-sm text-neutral-500">Loading…</div>}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {(packs ?? []).map((p) => {
            const ads = Math.floor(p.credits / CREDITS_PER_AD);
            return (
              <div key={p.id} className="flex flex-col rounded border border-neutral-200 p-4" data-testid={`topup-${p.id}`}>
                <div className="text-2xl font-bold">${(p.amountCents / 100).toFixed(0)}</div>
                <div className="mt-1 text-sm text-neutral-700">{p.credits.toLocaleString()} credits</div>
                <div className="mt-0.5 text-xs text-neutral-500">≈ {ads.toLocaleString()} ads</div>
                <button
                  type="button"
                  onClick={() => buy(p.id)}
                  disabled={buyingId !== null}
                  className="mt-4 block w-full rounded bg-black px-3 py-2 text-center text-sm text-white hover:bg-neutral-800 disabled:opacity-60"
                  data-testid={`topup-${p.id}-buy`}
                >
                  {buyingId === p.id ? "Starting…" : "Buy"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
